export type ViewMode = 'split' | 'focus' | string;

export interface Dimensions {
  width: number;
  height: number;
}

/**
 * Configuration for a screen/canvas in the game.
 */
export interface ScreenConfig {
  /** Unique ID for this screen */
  id: string;

  /** Layer ID this screen displays */
  layerId: string;

  /** Position in the layout (0 = top, higher = lower) */
  position: number;

  /** Display name for the view indicator */
  displayName?: string;
}

/**
 * Internal representation of a screen with its canvas and context.
 */
interface CanvasScreen {
  config: ScreenConfig;
  container: HTMLElement;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
}

/**
 * Layout options for dividing screen space among layers.
 */
export type LayoutMode = 'equal' | 'focus-first' | 'focus-last' | number[];

/**
 * Manages multiple canvas screens for game layers.
 * Supports dynamic screen creation and flexible layouts.
 */
export class GameCanvas {
  private gameContainer: HTMLElement | null = null;
  private screens: Map<string, CanvasScreen> = new Map();
  private screenOrder: string[] = [];
  private viewMode: ViewMode = 'split';
  private focusedLayerId: string | null = null;
  private viewIndicator: HTMLElement | null = null;
  private layoutMode: LayoutMode = 'equal';

  // Legacy compatibility: track which screen IDs map to top/bottom
  private topScreenId: string | null = null;
  private bottomScreenId: string | null = null;

  /**
   * Initialize the canvas system.
   * For backwards compatibility, looks for existing canvas elements.
   * For new usage, call addScreen() to create screens dynamically.
   */
  init(): void {
    this.gameContainer = document.getElementById('game-container');

    if (!this.gameContainer) {
      throw new Error('Game container not found');
    }

    // Check for existing canvas elements (backwards compatibility)
    const topCanvas = document.getElementById('parking-canvas') as HTMLCanvasElement;
    const bottomCanvas = document.getElementById('colony-canvas') as HTMLCanvasElement;

    if (topCanvas && bottomCanvas) {
      // Legacy mode: use existing HTML structure
      this.initLegacyScreens(topCanvas, bottomCanvas);
    }

    this.createViewIndicator();
  }

  /**
   * Initialize using existing HTML structure for backwards compatibility.
   */
  private initLegacyScreens(topCanvas: HTMLCanvasElement, bottomCanvas: HTMLCanvasElement): void {
    const topScreen = document.getElementById('top-screen');
    const bottomScreen = document.getElementById('bottom-screen');

    if (!topScreen || !bottomScreen) {
      throw new Error('Screen containers not found');
    }

    const topCtx = topCanvas.getContext('2d');
    const bottomCtx = bottomCanvas.getContext('2d');

    if (!topCtx || !bottomCtx) {
      throw new Error('Could not get canvas contexts');
    }

    // Register as screens
    this.topScreenId = 'parkington';
    this.bottomScreenId = 'formicarium';

    this.screens.set('parkington', {
      config: {
        id: 'parkington',
        layerId: 'parkington',
        position: 0,
        displayName: 'PARKINGTON',
      },
      container: topScreen,
      canvas: topCanvas,
      ctx: topCtx,
    });

    this.screens.set('formicarium', {
      config: {
        id: 'formicarium',
        layerId: 'formicarium',
        position: 1,
        displayName: 'FORMICARIUM',
      },
      container: bottomScreen,
      canvas: bottomCanvas,
      ctx: bottomCtx,
    });

    this.screenOrder = ['parkington', 'formicarium'];
  }

  /**
   * Add a new screen for a layer.
   * @param config Screen configuration
   */
  addScreen(config: ScreenConfig): void {
    if (this.screens.has(config.id)) {
      console.warn(`Screen ${config.id} already exists. Removing first.`);
      this.removeScreen(config.id);
    }

    if (!this.gameContainer) {
      throw new Error('GameCanvas not initialized');
    }

    // Create container
    const container = document.createElement('div');
    container.id = `screen-${config.id}`;
    container.className = 'screen';

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.id = `canvas-${config.id}`;

    container.appendChild(canvas);
    this.gameContainer.appendChild(container);

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      throw new Error(`Could not get canvas context for ${config.id}`);
    }

    const screen: CanvasScreen = {
      config,
      container,
      canvas,
      ctx,
    };

    this.screens.set(config.id, screen);

    // Update screen order
    this.updateScreenOrder();
  }

  /**
   * Remove a screen by ID.
   */
  removeScreen(id: string): void {
    const screen = this.screens.get(id);
    if (!screen) return;

    screen.container.remove();
    this.screens.delete(id);

    const orderIndex = this.screenOrder.indexOf(id);
    if (orderIndex !== -1) {
      this.screenOrder.splice(orderIndex, 1);
    }
  }

  /**
   * Update the order of screens based on their position config.
   */
  private updateScreenOrder(): void {
    this.screenOrder = Array.from(this.screens.values())
      .sort((a, b) => a.config.position - b.config.position)
      .map(s => s.config.id);
  }

  /**
   * Set the layout mode for screen distribution.
   */
  setLayout(layout: LayoutMode): void {
    this.layoutMode = layout;
    this.applyLayout();
  }

  /**
   * Apply the current layout mode to screen containers.
   */
  private applyLayout(): void {
    if (this.viewMode !== 'split') {
      // Focus mode - only one screen visible
      return;
    }

    const screens = this.screenOrder.map(id => this.screens.get(id)!);
    let heights: number[];

    if (Array.isArray(this.layoutMode)) {
      heights = this.layoutMode;
    } else if (this.layoutMode === 'equal') {
      heights = screens.map(() => 100 / screens.length);
    } else if (this.layoutMode === 'focus-first') {
      heights = screens.map((_, i) => i === 0 ? 60 : 40 / (screens.length - 1));
    } else if (this.layoutMode === 'focus-last') {
      heights = screens.map((_, i) => i === screens.length - 1 ? 60 : 40 / (screens.length - 1));
    } else {
      heights = screens.map(() => 100 / screens.length);
    }

    screens.forEach((screen, i) => {
      screen.container.style.height = `${heights[i]}%`;
    });
  }

  private createViewIndicator(): void {
    this.viewIndicator = document.createElement('div');
    this.viewIndicator.id = 'view-indicator';
    this.viewIndicator.style.cssText = `
      position: fixed;
      bottom: 10px;
      right: 10px;
      padding: 8px 12px;
      background: rgba(0, 0, 0, 0.7);
      color: white;
      font-family: 'Segoe UI', system-ui, sans-serif;
      font-size: 12px;
      border-radius: 4px;
      z-index: 100;
      pointer-events: none;
      transition: opacity 0.3s;
    `;
    document.body.appendChild(this.viewIndicator);
    this.updateViewIndicator();
  }

  private updateViewIndicator(): void {
    if (!this.viewIndicator) return;

    if (this.viewMode === 'split') {
      this.viewIndicator.textContent = 'SPLIT VIEW [TAB]';
    } else if (this.focusedLayerId) {
      const screen = this.screens.get(this.focusedLayerId);
      const name = screen?.config.displayName || this.focusedLayerId.toUpperCase();
      this.viewIndicator.textContent = `${name} [TAB]`;
    }
  }

  resize(): void {
    const dpr = window.devicePixelRatio || 1;

    for (const screen of this.screens.values()) {
      const rect = screen.container.getBoundingClientRect();

      // Reset transform before resizing
      screen.ctx.setTransform(1, 0, 0, 1, 0, 0);
      screen.canvas.width = rect.width * dpr;
      screen.canvas.height = rect.height * dpr;
      screen.ctx.scale(dpr, dpr);
    }
  }

  /**
   * Set view mode for N screens.
   * 'split' shows all screens, 'focus' shows only the focused layer.
   */
  setViewMode(mode: ViewMode): void {
    this.viewMode = mode;

    if (this.gameContainer) {
      // For legacy compatibility with CSS classes
      if (mode === 'split') {
        this.gameContainer.className = 'split';
        this.focusedLayerId = null;
      } else if (mode === 'full-top' && this.topScreenId) {
        this.gameContainer.className = 'full-top';
        this.focusedLayerId = this.topScreenId;
      } else if (mode === 'full-bottom' && this.bottomScreenId) {
        this.gameContainer.className = 'full-bottom';
        this.focusedLayerId = this.bottomScreenId;
      } else {
        // Dynamic mode for N screens
        this.focusedLayerId = mode;
        this.gameContainer.className = `focus-${mode}`;
      }
    }

    this.updateViewIndicator();
  }

  /**
   * Focus on a specific layer (for N-layer support).
   */
  focusLayer(layerId: string): void {
    if (!this.screens.has(layerId)) {
      console.warn(`Cannot focus on unknown layer: ${layerId}`);
      return;
    }

    this.focusedLayerId = layerId;
    this.viewMode = layerId;

    // Update container visibility
    for (const [id, screen] of this.screens) {
      if (id === layerId) {
        screen.container.style.height = '100%';
        screen.container.style.display = 'block';
      } else {
        screen.container.style.height = '0';
        screen.container.style.display = 'none';
      }
    }

    this.updateViewIndicator();
  }

  /**
   * Show all layers in split view.
   */
  showSplit(): void {
    this.viewMode = 'split';
    this.focusedLayerId = null;

    // Restore all screens
    for (const screen of this.screens.values()) {
      screen.container.style.display = 'block';
    }

    this.applyLayout();
    this.updateViewIndicator();
  }

  /**
   * Cycle to the next view mode.
   * Returns the new view mode.
   */
  cycleViewMode(): ViewMode {
    const modes: ViewMode[] = ['split', ...this.screenOrder];
    const currentIndex = this.viewMode === 'split'
      ? 0
      : modes.indexOf(this.focusedLayerId || '');

    const nextIndex = (currentIndex + 1) % modes.length;
    const nextMode = modes[nextIndex];

    if (nextMode === 'split') {
      this.showSplit();
    } else {
      this.focusLayer(nextMode);
    }

    // For legacy compatibility, update container class
    if (this.gameContainer && this.screenOrder.length === 2) {
      if (nextMode === 'split') {
        this.gameContainer.className = 'split';
      } else if (nextMode === this.screenOrder[0]) {
        this.gameContainer.className = 'full-top';
      } else {
        this.gameContainer.className = 'full-bottom';
      }
    }

    return this.viewMode;
  }

  getViewMode(): ViewMode {
    return this.viewMode;
  }

  getFocusedLayerId(): string | null {
    return this.focusedLayerId;
  }

  /**
   * Get the layer that should receive input based on current view.
   * In split mode, returns null (caller should determine based on mouse position).
   * In focus mode, returns the focused layer.
   */
  getActiveInputLayerId(): string | null {
    return this.focusedLayerId;
  }

  /**
   * Get context for a specific layer.
   */
  getContext(layerId: string): CanvasRenderingContext2D | null {
    return this.screens.get(layerId)?.ctx || null;
  }

  /**
   * Get all contexts as a map (for LayerManager.render).
   */
  getContexts(): Map<string, CanvasRenderingContext2D> {
    const contexts = new Map<string, CanvasRenderingContext2D>();
    for (const [id, screen] of this.screens) {
      contexts.set(screen.config.layerId, screen.ctx);
    }
    return contexts;
  }

  /**
   * Get dimensions for a specific layer.
   */
  getDimensions(layerId: string): Dimensions {
    const screen = this.screens.get(layerId);
    if (!screen) return { width: 0, height: 0 };
    const rect = screen.container.getBoundingClientRect();
    return { width: rect.width, height: rect.height };
  }

  /**
   * Get dimensions for all layers.
   */
  getAllDimensions(): Map<string, Dimensions> {
    const dimensions = new Map<string, Dimensions>();
    for (const [id, screen] of this.screens) {
      const rect = screen.container.getBoundingClientRect();
      dimensions.set(screen.config.layerId, { width: rect.width, height: rect.height });
    }
    return dimensions;
  }

  /**
   * Get canvas for a specific layer.
   */
  getCanvas(layerId: string): HTMLCanvasElement | null {
    return this.screens.get(layerId)?.canvas || null;
  }

  /**
   * Get the screen order (layer IDs from top to bottom).
   */
  getScreenOrder(): string[] {
    return [...this.screenOrder];
  }

  // Legacy compatibility methods
  getTopContext(): CanvasRenderingContext2D | null {
    return this.topScreenId ? this.getContext(this.topScreenId) : null;
  }

  getBottomContext(): CanvasRenderingContext2D | null {
    return this.bottomScreenId ? this.getContext(this.bottomScreenId) : null;
  }

  getTopCanvas(): HTMLCanvasElement | null {
    return this.topScreenId ? this.getCanvas(this.topScreenId) : null;
  }

  getBottomCanvas(): HTMLCanvasElement | null {
    return this.bottomScreenId ? this.getCanvas(this.bottomScreenId) : null;
  }

  getTopDimensions(): Dimensions {
    return this.topScreenId ? this.getDimensions(this.topScreenId) : { width: 0, height: 0 };
  }

  getBottomDimensions(): Dimensions {
    return this.bottomScreenId ? this.getDimensions(this.bottomScreenId) : { width: 0, height: 0 };
  }
}
